import {
    Component,
    Element,
    Host,
    Listen,
    Prop,
    State,
    h,
} from "@stencil/core";
import { ActiveHotSpotChangeEventData } from "./hot-spot-container";

/**
 * HotSpot component that can be placed on an image
 */
@Component({
    tag: "ux-iws-hot-spot",
    styleUrl: "hot-spot.scss",
    shadow: {
        delegatesFocus: true,
    },
})
export class HotSpot {
    private titleContainer: HTMLSpanElement;
    @Element() element: HTMLUxIwsHotSpotElement;

    /**
     * unique identifier for the HotSpot
     */
    @Prop() hotSpotId: string;

    /**
     * left position of the HotSpot in percent
     */
    @Prop() left: number;

    /**
     * top position of the HotSpot in percent
     */
    @Prop() top: number;

    /**
     * Number displayed on the HotSpot
     */
    @Prop() number: string;

    /**
     * Icon name that will be displayed instead of the number - must be a valid icon name from the UX Icon library
     */
    @Prop() icon: string = "";

    /**
     * Show a bullet icon instead of the number
     */
    @Prop() bullet: boolean = false;

    @State() active: boolean = false;

    @State() titleWidth: number;

    private onClick() {
        const container = this.element.closest("ux-iws-hot-spot-container");
        if (container) {
            container.activeHotSpot = this.hotSpotId;
        }
    }

    @Listen("uxActiveHotSpotChange", { target: "body" })
    activeHotSpotChange(event: CustomEvent<ActiveHotSpotChangeEventData>) {
        this.active = event.detail.activeHotSpot === this.hotSpotId;
    }

    componentDidLoad() {
        const container = this.element.closest("ux-iws-hot-spot-container");
        if (container) {
            this.active = container.activeHotSpot === this.hotSpotId;
        }
        this.titleWidth = this.titleContainer.getBoundingClientRect().width;
    }

    render() {
        const left = `${this.left}%`;
        const top = `${this.top}%`;
        const hasIcon = this.icon !== "";
        return (
            <Host style={{ top, left }}>
                <button
                    class={{
                        button: true,
                        "button--active": this.active,
                        "button--no-title": this.titleWidth === 0,
                        "button--icon": hasIcon,
                    }}
                    type="button"
                    onClick={() => this.onClick()}
                    style={{
                        width: `${this.titleWidth + 50}px`,
                    }}
                    data-testid={`hot-spot-${this.hotSpotId}`}
                >
                    <span class="button__inner">
                        {this.bullet ? (
                            <span class="label label--bullet"></span>
                        ) : hasIcon ? (
                            <span class="label label--icon">
                                <ux-icon
                                    name={this.icon}
                                    class="icon"
                                ></ux-icon>
                            </span>
                        ) : (
                            <span class="label label--number">
                                {this.number}
                            </span>
                        )}
                        <span
                            class="title"
                            ref={(el) => (this.titleContainer = el)}
                        >
                            <slot></slot>
                        </span>
                    </span>
                </button>
            </Host>
        );
    }
}
